import React, { Component, ErrorInfo, ReactNode } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle,
  RefreshCw,
  Bug,
  Copy,
  CheckCircle,
  XCircle,
  AlertCircle,
} from "lucide-react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetTrigger?: unknown;
  integrationType?: string;
  integrationId?: string;
  integrationContext?: {
    type: string;
    name: string;
    id: string;
  };
  maxRetries?: number;
  retryDelay?: number;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string;
  retryCount: number;
  lastErrorTime: number;
  copied: boolean;
  waitingForRetry: boolean;
}

class IntegrationErrorBoundary extends Component<Props, State> {
  private retryTimeoutId: NodeJS.Timeout | null = null;
  private copyTimeoutId: NodeJS.Timeout | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: "",
      retryCount: 0,
      lastErrorTime: 0,
      copied: false,
      waitingForRetry: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    const errorId = `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    return {
      hasError: true,
      error,
      errorId,
      lastErrorTime: Date.now(),
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    this.reportError(error, errorInfo);

    if (this.shouldAutoRetry(error)) {
      this.scheduleRetry();
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (
      this.state.hasError &&
      (this.props.resetTrigger !== prevProps.resetTrigger ||
        this.props.children !== prevProps.children)
    ) {
      this.resetError();
    }
  }

  componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
    if (this.copyTimeoutId) {
      clearTimeout(this.copyTimeoutId);
    }
  }

  private getMaxRetries(): number {
    return this.props.maxRetries ?? 3;
  }

  private getRetryDelay(): number {
    return this.props.retryDelay ?? 2000;
  }

  private shouldAutoRetry(error: Error): boolean {
    const { retryCount } = this.state;

    if (retryCount >= this.getMaxRetries()) {
      return false;
    }

    const message = error.message.toLowerCase();
    const retryableErrors = [
      "network error",
      "failed to fetch",
      "timeout",
      "request timeout",
      "connection refused",
      "rate limit",
      "rate limit exceeded",
      "service unavailable",
    ];

    return retryableErrors.some((errorType) => message.includes(errorType));
  }

  private scheduleRetry = () => {
    const delay = this.getRetryDelay() * Math.pow(2, this.state.retryCount);

    this.setState({ waitingForRetry: true });

    this.retryTimeoutId = setTimeout(() => {
      this.handleRetry();
    }, delay);
  };

  private resetError = () => {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }
    if (this.copyTimeoutId) {
      clearTimeout(this.copyTimeoutId);
      this.copyTimeoutId = null;
    }

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: "",
      retryCount: 0,
      lastErrorTime: 0,
      copied: false,
      waitingForRetry: false,
    });
  };

  private handleRetry = () => {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }

    this.setState((prevState) => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1,
      copied: false,
      waitingForRetry: false,
    }));
  };

  private reportError = (error: Error, errorInfo: ErrorInfo) => {
    const errorReport = {
      error: {
        message: error.message,
        stack: error.stack,
        name: error.name,
      },
      errorInfo,
      context: {
        integration: this.props.integrationContext,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
      },
      errorId: this.state.errorId,
      timestamp: new Date().toISOString(),
      integrationType: this.props.integrationType || "unknown",
      integrationId: this.props.integrationId || "unknown",
    };

    try {
      localStorage.setItem("integration_errors", JSON.stringify(errorReport));
    } catch (e) {
      console.warn("Não foi possível salvar erro no localStorage:", e);
    }
  };

  private copyErrorToClipboard = async () => {
    const errorText = `
Error ID: ${this.state.errorId}
Integration: ${this.props.integrationContext?.name || "Unknown"}
Error: ${this.state.error?.message}
Stack: ${this.state.error?.stack}
Component Stack: ${this.state.errorInfo?.componentStack}
Time: ${new Date(this.state.lastErrorTime).toISOString()}
    `.trim();

    try {
      await navigator.clipboard.writeText(errorText);
      this.setState({ copied: true });
      this.copyTimeoutId = setTimeout(() => {
        this.setState({ copied: false });
      }, 2000);
    } catch (err) {
      console.warn(
        "Não foi possível copiar erro para a área de transferência:",
        err,
      );
    }
  };

  private getErrorSeverity = (): "low" | "medium" | "high" => {
    if (!this.state.error) return "low";

    const message = this.state.error.message.toLowerCase();

    if (
      message.includes("network") ||
      message.includes("timeout") ||
      message.includes("fetch")
    ) {
      return "medium";
    }

    if (message.includes("syntax") || message.includes("reference")) {
      return "high";
    }

    return "low";
  };

  private getErrorCategory = (): string => {
    if (!this.state.error) return "Desconhecido";

    const message = this.state.error.message.toLowerCase();

    if (message.includes("network") || message.includes("fetch")) {
      return "Conectividade";
    }

    if (message.includes("syntax")) {
      return "Erro de Código";
    }

    if (message.includes("permission") || message.includes("auth")) {
      return "Autenticação";
    }

    if (message.includes("rate limit") || message.includes("quota")) {
      return "Limite de Taxa";
    }

    return "Desconhecido";
  };

  private getSeverityIcon = () => {
    const severity = this.getErrorSeverity();

    switch (severity) {
      case "high":
        return <XCircle className="h-5 w-5 text-red-500" />;
      case "medium":
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      default:
        return <AlertTriangle className="h-5 w-5 text-orange-500" />;
    }
  };

  private getSeverityColor = () => {
    const severity = this.getErrorSeverity();

    switch (severity) {
      case "high":
        return "bg-red-50 border-red-200";
      case "medium":
        return "bg-yellow-50 border-yellow-200";
      default:
        return "bg-orange-50 border-orange-200";
    }
  };

  private getIntegrationContext = () => {
    if (this.props.integrationContext) {
      return this.props.integrationContext;
    }

    if (!this.props.integrationType && !this.props.integrationId) {
      return undefined;
    }

    const rawType = this.props.integrationType ?? "general";
    const name = rawType
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

    return {
      type: rawType,
      name,
      id: this.props.integrationId ?? rawType,
    };
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const integration = this.getIntegrationContext();
      const severity = this.getErrorSeverity();
      const category = this.getErrorCategory();
      const maxRetries = this.getMaxRetries();
      const canRetry = this.state.retryCount < maxRetries;

      return (
        <div className="p-4 space-y-4" data-testid="error-boundary">
          <Card
            className={`border-2 ${this.getSeverityColor()}`}
            role="alert"
            aria-live="polite"
            data-testid={
              severity === "medium" ? "error-severity-medium" : undefined
            }
          >
            <CardHeader>
              <div className="flex items-center gap-3">
                <div data-testid="error-severity-icon">
                  {this.getSeverityIcon()}
                </div>
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    Ops! Algo deu errado
                    <Badge variant="outline" className="text-xs">
                      Categoria: {category}
                    </Badge>
                  </CardTitle>
                  <CardDescription>
                    {integration
                      ? `${integration.name} (${integration.type})`
                      : "Integração não identificada"}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Ocorreu um erro inesperado. Por favor, tente novamente.
              </p>

              <Alert role="none">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="font-medium">
                  {this.state.error?.message || "Erro desconhecido"}
                </AlertDescription>
              </Alert>

              {this.state.waitingForRetry && (
                <Alert role="none">
                  <RefreshCw className="h-4 w-4" />
                  <AlertDescription>
                    Tentando reconectar automaticamente...
                  </AlertDescription>
                </Alert>
              )}

              {this.state.retryCount > 0 && !this.state.waitingForRetry && (
                <Alert role="none">
                  <RefreshCw className="h-4 w-4" />
                  <AlertDescription>
                    Tentativas de recuperação: {this.state.retryCount}/
                    {maxRetries}
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={this.resetError}
                  variant="default"
                  className="flex items-center gap-2"
                  aria-label="Tentar novamente"
                >
                  <RefreshCw className="h-4 w-4" />
                  Tentar Novamente
                </Button>

                {canRetry && (
                  <Button
                    onClick={this.handleRetry}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Retry Automático
                  </Button>
                )}

                <Button
                  onClick={this.copyErrorToClipboard}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <Copy className="h-4 w-4" />
                  {this.state.copied ? "Copiado!" : "Copiar Detalhes"}
                </Button>

                {process.env.NODE_ENV === "development" && (
                  <Button
                    onClick={() => {}}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <Bug className="h-4 w-4" />
                    Debug
                  </Button>
                )}
              </div>

              {process.env.NODE_ENV === "development" && (
                <details className="mt-4">
                  <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                    Detalhes Técnicos
                  </summary>
                  <div className="mt-2 p-3 bg-gray-50 rounded-md text-xs font-mono">
                    <div>
                      <strong>Error ID:</strong> {this.state.errorId}
                    </div>
                    <div>
                      <strong>Time:</strong>{" "}
                      {new Date(this.state.lastErrorTime).toISOString()}
                    </div>
                    <div>
                      <strong>Message:</strong> {this.state.error?.message}
                    </div>
                    {this.state.error?.stack && (
                      <div>
                        <strong>Stack:</strong>
                        <pre className="mt-1 whitespace-pre-wrap text-xs">
                          {this.state.error.stack}
                        </pre>
                      </div>
                    )}
                    {this.state.errorInfo?.componentStack && (
                      <div>
                        <strong>Component Stack:</strong>
                        <pre className="mt-1 whitespace-pre-wrap text-xs">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              )}

              <div className="p-3 bg-blue-50 rounded-md border border-blue-200">
                <h4 className="font-medium text-blue-900 mb-2">
                  💡 Sugestões para Resolver
                </h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  {severity === "high" && (
                    <li>• Verifique se há erros de sintaxe no código</li>
                  )}
                  {category === "Conectividade" && (
                    <>
                      <li>• Verifique sua conexão com a internet</li>
                      <li>• Tente novamente em alguns minutos</li>
                    </>
                  )}
                  {category === "Autenticação" && (
                    <>
                      <li>• Verifique as permissões de acesso</li>
                      <li>• Verifique suas credenciais de acesso</li>
                      <li>• Reconecte a integração se necessário</li>
                    </>
                  )}
                  {category === "Limite de Taxa" && (
                    <>
                      <li>
                        • Aguarde alguns minutos antes de tentar novamente
                      </li>
                      <li>• Considere reduzir a frequência de sincronização</li>
                    </>
                  )}
                  <li>• Se o problema persistir, contate o suporte técnico</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default IntegrationErrorBoundary;
